import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { DEFAULT_LOCALE, getString, normalizeLocale } from "./translations";

const STORAGE_KEY = "borealis.locale";

const LanguageContext = createContext(null);

function readInitialLocale() {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  const fromStorage = window.localStorage.getItem(STORAGE_KEY);
  if (fromStorage) return normalizeLocale(fromStorage);
  const fromBrowser = window.navigator?.language;
  return normalizeLocale(fromBrowser);
}

export function LanguageProvider({ children }) {
  const { user, refresh } = useAuth();
  const [locale, setLocaleState] = useState(readInitialLocale);

  // When the authenticated user's locale changes (e.g. just logged in),
  // prefer the server-stored locale over the localStorage one.
  useEffect(() => {
    if (user && typeof user === "object" && user.locale) {
      const normalized = normalizeLocale(user.locale);
      if (normalized !== locale) {
        setLocaleState(normalized);
        if (typeof window !== "undefined") {
          window.localStorage.setItem(STORAGE_KEY, normalized);
        }
      }
    }
  }, [user, locale]);

  const setLocale = useCallback(
    async (next) => {
      const normalized = normalizeLocale(next);
      setLocaleState(normalized);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(STORAGE_KEY, normalized);
      }
      // If authenticated, persist on the user record so future emails use it.
      if (user && typeof user === "object") {
        try {
          await api.patch("/auth/me", { locale: normalized });
          await refresh?.();
        } catch (err) {
          // Non-fatal — local UI continues in the new locale.
          console.warn("Failed to persist locale to server:", err?.message || err);
        }
      }
    },
    [user, refresh]
  );

  const t = useCallback((key, vars) => getString(locale, key, vars), [locale]);

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useI18n must be used within LanguageProvider");
  return ctx;
}
